import { Component, ChangeDetectionStrategy, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChartModule } from '@progress/kendo-angular-charts';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { GridModule, ExcelModule } from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { FormsModule } from '@angular/forms';
import { SVGIcon, filterIcon, pencilIcon, exportIcon } from '@progress/kendo-svg-icons';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { VssAnalyticsService } from './services/vss-analytics.service';
import { SLATargetsService } from './services/sla-targets.service';

import {
  VssOverviewDataResponse,
  ProblemChildPatchesResponse,
  CriticalVulnerabilityAgingResponse,
  AverageCoverageResponse,
  AverageComplianceResponse,
  AverageMeanTimeToMitigateResponse,
  VulnerabilityEventsResponse,
  DeviceTypeChartResponse,
  MttmAnalysisResponse,
  SLATargetsResponse
} from '../../../../../core/models/analytics/vss-models';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { NumericTextBoxModule } from '@progress/kendo-angular-inputs';
import { KENDO_PDFEXPORT } from '@progress/kendo-angular-pdf-export';

// Import child components
import { VulnerabilityOverviewComponent } from './components/vulnerability-overview/vulnerability-overview.component';
import { CriticalVulnerabilityAgingComponent } from './components/critical-vulnerability-aging/critical-vulnerability-aging.component';
import { ProblemChildPatchesComponent } from './components/problem-child-patches/problem-child-patches.component';
import { VulnerabilityEventsChartComponent } from './components/vulnerability-events-chart/vulnerability-events-chart.component';
import { DeviceTypeChartComponent } from './components/device-type-chart/device-type-chart.component';
import { MttmAnalysisComponent } from './components/mttm-analysis/mttm-analysis.component';
import { FilterHeaderComponent } from './components/filter-header/filter-header.component';
import { SlaDialogComponent } from './components/sla-dialog/sla-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs';
import { VssComplianceCardComponent } from './components/vss-compliance-card/vss-compliance-card.component';
import { VssCoverageCardComponent } from './components/vss-coverage-card/vss-coverage-card.component';
import { VssMttmCardComponent } from './components/vss-mttm-card/vss-mttm-card.component';

@Component({
  selector: 'app-vss',
  standalone: true,
  imports: [
    CommonModule,
    ChartModule,
    TabStripModule,
    GridModule,
    IconsModule,
    ButtonsModule,
    DropDownsModule,
    DatePickerModule,
    FormsModule,
    ExcelModule,
    DialogsModule,
    NumericTextBoxModule,
    KENDO_PDFEXPORT,
    // Child components
    VulnerabilityOverviewComponent,
    CriticalVulnerabilityAgingComponent,
    ProblemChildPatchesComponent,
    VulnerabilityEventsChartComponent,
    DeviceTypeChartComponent,
    MttmAnalysisComponent,
    FilterHeaderComponent,
    SlaDialogComponent,
    VssComplianceCardComponent,
    VssMttmCardComponent,
    VssCoverageCardComponent,
  ],
  templateUrl: './vss.component.html',
  styleUrl: './vss.component.scss',
  providers: [VssAnalyticsService, SLATargetsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VssComponent extends BaseComponent {
  private vssDetailsService = inject(VssAnalyticsService);
  private slaTargetsService = inject(SLATargetsService);

  public organizationId: number | null = null;
  private route = inject(ActivatedRoute);
  public startDate: Date = new Date(new Date().setDate(new Date().getDate() - 365));
  public endDate: Date = new Date();
  public startDateString: string = '';
  public endDateString: string = '';
  public activeTab = 'overview';
  public filterIcon: SVGIcon = filterIcon;
  public pencilIcon: SVGIcon = pencilIcon;
  public exportIcon: SVGIcon = exportIcon;
  public Math = Math;

  // VSS data signals
  public vssOverviewDataResponse = signal(new VssOverviewDataResponse());
  public problemChildPatchesResponse = signal(new ProblemChildPatchesResponse());
  public criticalVulnerabilityAgingResponse = signal(new CriticalVulnerabilityAgingResponse());
  public averageCoverageResponse = signal(new AverageCoverageResponse());
  public averageComplianceResponse = signal(new AverageComplianceResponse());
  public averageMeanTimeToMitigateResponse = signal(new AverageMeanTimeToMitigateResponse());
  public vulnerabilityEventsResponse = signal(new VulnerabilityEventsResponse());
  public deviceTypeChartResponse = signal(new DeviceTypeChartResponse());
  public mttmAnalysisResponse = signal(new MttmAnalysisResponse());
  public slaTargetsResponse = signal(new SLATargetsResponse());

  // Problem child patches filters signal
  public problemChildFilters = signal<any>({});

  // Filter properties
  public timeRanges: Array<{ text: string; value: string }> = [
    { text: 'Last 24 Hours', value: '24h' },
    { text: 'Last 7 Days', value: '7d' },
    { text: 'Last 30 Days', value: '30d' },
    { text: 'Custom', value: 'custom' },
  ];

  public selectedTimeRange: { text: string; value: string } = { text: 'Last 30 days', value: '30d' };

  public editingSLA = {
    critical: false,
    high: false,
  };

  public tempSLAValues: {critical: number | null, high: number | null} = {
    critical: null,
    high: null,
  };

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect(): void {
    effect(
      () => {
        this.route.queryParams
          .pipe(
            filter((params) => params['organizationId']),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: (params) => {
              this.organizationId = +params['organizationId'];
              this.applyFilters();
            },
            error: (error) => {
              console.error('Error processing query parameters:', error);
            },
          });
      },
      { allowSignalWrites: true }
    );
    this.applyFilters(); // This is the initial call to get the data
  }

  // Filter methods
  public applyFilters(): void {
    const params = this.selectedTimeRange.value;

    if (params === '7d') {
      this.startDate = new Date(new Date().setDate(new Date().getDate() - 7));
      this.endDate = new Date();
    } else if (params === '30d') {
      this.startDate = new Date(new Date().setDate(new Date().getDate() - 30));
      this.endDate = new Date();
    } else if (params === '90d') {
      this.startDate = new Date(new Date().setDate(new Date().getDate() - 90));
      this.endDate = new Date();
    } else if (params == "24h"){
      this.startDate = new Date(new Date().setDate(new Date().getDate() - 1));
      this.endDate = new Date();
    }

    if (this.organizationId && this.startDate && this.endDate) {
      this.getAllVssData(this.organizationId, params);
      this.startDateString = this.startDate.toISOString();
      this.endDateString = this.endDate.toISOString();
      // Update the filters signal that will be passed to the child component
      this.problemChildFilters.set({
        "current_start": this.startDateString,
        "current_end": this.endDateString
      });
    }
  }

  public onTimeRangeChange(selectedRange: any): void {
    this.selectedTimeRange = selectedRange;
    // Removed applyFilters() call - it will only be called by the button now
  }

  // Update startDate when the date picker value changes
  public onStartDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(0, 0, 0, 0);
      this.startDate = newDate;
    }
    // No API call here
  }

  // Update endDate when the date picker value changes
  public onEndDateChange(newDate: Date | null): void {
    if (newDate) {
      newDate.setHours(23, 59, 59, 999);
      this.endDate = newDate;
    }
    // No API call here
  }

  private getAllVssData(_orgIds: number, _params: any) {
    this.getVssOverviewData();
    this.getCriticalVulnerabilityAging();
    this.getAverageCoverage();
    this.getAverageCompliance();
    this.getAverageMeanTimeToMitigate();
    this.getVulnerabilityEvents();
    this.getDeviceTypeChart();
    this.getMttmAnalysis();
    this.getSLATargets();
  }

  private buildApiParams(): any {
    const params: any = {};

    if (this.startDate) {
      params.current_start = this.startDate.toISOString();
    }

    if (this.endDate) {
      params.current_end = this.endDate.toISOString();
    }

    if (this.selectedTimeRange.value !== 'custom') {
      params.time_range = this.selectedTimeRange.value;
    }

    return params;
  }

  private getVssOverviewData() {
    const params = this.buildApiParams();
    this.vssDetailsService.getVssOverviewData(this.organizationId, params).subscribe({
      next: (data) => this.vssOverviewDataResponse.set(data),
      error: (error) => console.error('Error fetching VSS overview data:', error),
    });
  }

  private getCriticalVulnerabilityAging() {
    const params = this.buildApiParams();
    this.vssDetailsService.getCriticalVulnerabilityAging(this.organizationId, params).subscribe({
      next: (data) => this.criticalVulnerabilityAgingResponse.set(data),
      error: (error) => console.error('Error fetching critical vulnerability aging:', error),
    });
  }

  private getAverageCoverage() {
    const params = this.buildApiParams();
    this.vssDetailsService.getAverageCoverage(this.organizationId!, params).subscribe({
      next: (data) => this.averageCoverageResponse.set(data),
      error: (error) => console.error('Error fetching time series metrics:', error),
    });
  }

  private getAverageCompliance() {
    const params = this.buildApiParams();
    this.vssDetailsService.getAverageCompliance(this.organizationId, params).subscribe({
      next: (data) => this.averageComplianceResponse.set(data),
      error: (error) => console.error('Error fetching average compliance:', error),
    });
  }

  private getAverageMeanTimeToMitigate() {
    const params = this.buildApiParams();
    this.vssDetailsService.getAverageMeanTimeToMitigate(this.organizationId!, params).subscribe({
      next: (data) => this.averageMeanTimeToMitigateResponse.set(data),
      error: (error) => console.error('Error fetching average mean time to mitigate:', error),
    });
  }

  private getVulnerabilityEvents() {
    const params = this.buildApiParams();
    this.vssDetailsService.getVulnerabilityEvents(this.organizationId, params).subscribe({
      next: (data) => this.vulnerabilityEventsResponse.set(data),
      error: (error) => console.error('Error fetching vulnerability events:', error),
    });
  }

  private getDeviceTypeChart() {
    const params = this.buildApiParams();
    this.vssDetailsService.getDeviceTypeChart(this.organizationId, params).subscribe({
      next: (data) => this.deviceTypeChartResponse.set(data),
      error: (error) => console.error('Error fetching device type chart:', error),
    });
  }

  private getMttmAnalysis() {
    const params = this.buildApiParams();
    this.vssDetailsService.getMttmAnalysis(this.organizationId, params).subscribe({
      next: (data) => this.mttmAnalysisResponse.set(data),
      error: (error) => console.error('Error fetching MTTM analysis:', error),
    });
  }

  private getSLATargets() {
    this.slaTargetsService.getSLATargets(this.organizationId!).subscribe({
      next: (data) => this.slaTargetsResponse.set(data),
      error: (error) => console.error('Error fetching SLA setting:', error),
    });
  }

  public startSLAEdit(type: 'critical' | 'high' | 'both'): void {
    if (type === 'both') {
      this.editingSLA.critical = true;
      this.editingSLA.high = true;
    } else {
      this.editingSLA[type] = true;
    }
    this.tempSLAValues.critical = this.slaTargetsResponse().critical;
    this.tempSLAValues.high = this.slaTargetsResponse().high;
  }

  public saveSLA(type: 'critical' | 'high' | 'both'): void {
    const isValidCritical = this.tempSLAValues.critical != null && this.tempSLAValues.critical >= 1 && this.tempSLAValues.critical <= 90;
    const isValidHigh = this.tempSLAValues.high != null && this.tempSLAValues.high >= 1 && this.tempSLAValues.high <= 90;

    if (type === 'both' && isValidCritical && isValidHigh) {
      this.slaTargetsResponse().critical = this.tempSLAValues.critical;
      this.slaTargetsResponse().high = this.tempSLAValues.high;
      this.editingSLA.critical = false;
      this.editingSLA.high = false;
    } else if (type === 'critical' && isValidCritical) {
      this.slaTargetsResponse().critical = this.tempSLAValues.critical;
      this.editingSLA.critical = false;
    } else if (type === 'high' && isValidHigh) {
      this.slaTargetsResponse().high = this.tempSLAValues.high;
      this.editingSLA.high = false;
    }

    this.slaTargetsService.saveSLATargets(this.organizationId!, this.slaTargetsResponse()).subscribe({
      next: (response) => {
        this.slaTargetsResponse.set({
          ...this.slaTargetsResponse(),  // keep old values
          critical: response.critical,
          high: response.high
        });
      },
        error: (error) => {
          console.error('Error updating SLA Targets:', error);
        },
      });
  }

  public cancelSLAEdit(type: 'critical' | 'high' | 'both'): void {
    if (type === 'both') {
      this.editingSLA.critical = false;
      this.editingSLA.high = false;
    } else {
      this.editingSLA[type] = false;
    }
    this.tempSLAValues.critical = this.slaTargetsResponse().critical;
    this.tempSLAValues.high = this.slaTargetsResponse().high;
  }
}
